package com.islamicmedia.site.components

import androidx.compose.runtime.*
import com.islamicmedia.shared.AppConfig
import com.islamicmedia.shared.data.api.createDefaultApiClient
import com.islamicmedia.shared.data.auth.SupabaseAuthService
import com.islamicmedia.shared.domain.model.BillingSummary
import kotlinx.coroutines.await
import com.islamicmedia.shared.domain.model.MediaType
import com.islamicmedia.shared.presentation.HistoryItem
import com.islamicmedia.site.theme.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Percent-encode a value for safe embedding in a URL fragment/query segment. Kotlin/JS has no
 * stdlib wrapper for this, so we call the browser global directly — mirrors the private
 * declaration in Admin.kt, which isn't visible from this file.
 */
private external fun encodeURIComponent(value: String): String

@Composable
fun ProfileScreen(
    userEmail: String?,
    userPhotoUrl: String?,
    userName: String? = null,
    isSubscribed: Boolean,
    entitlement: com.islamicmedia.shared.domain.model.Entitlement? = null,
    history: List<HistoryItem>,
    onManageSubscription: () -> Unit,
    /** Card on file + invoices, loaded at app open. Null while loading OR when the fetch failed —
     *  [billingLoaded] is what separates those two. */
    billingSummary: BillingSummary? = null,
    /** True once the account-details call has come back, successfully or not. */
    billingLoaded: Boolean = false,
    /** Null means "we couldn't find out", which hides the toggle rather than guessing its position. */
    emailMarketing: Boolean? = null,
    onEmailMarketingChanged: (Boolean) -> Unit = {},
    /** Re-read the card and invoices after something changed them. */
    onRefreshAccountDetails: () -> Unit = {},
    onDeleteAccount: () -> Unit,
    onHistoryItemClick: (HistoryItem) -> Unit = {},
    /** Re-fetch the entitlement after the cancellation flow changes it. */
    onEntitlementChanged: () -> Unit = {},
    /** End a pause early. Only reachable while the membership is paused. */
    onResumeSubscription: () -> Unit = {},
    billingLoading: Boolean = false,
    onPlanClick: () -> Unit = {},
    onGetCredits: () -> Unit = {},
    plans: List<com.islamicmedia.shared.domain.model.PricingPlan> = emptyList(),
    displaySettings: com.islamicmedia.shared.domain.model.DisplaySettings = com.islamicmedia.shared.domain.model.DisplaySettings.DEFAULT,
    onDisplaySettingsChanged: (com.islamicmedia.shared.domain.model.DisplaySettings) -> Unit = {},
    startOnSettings: Boolean = false,
    startOnAccount: Boolean = false
) {
    var showDeleteConfirmation by remember { mutableStateOf(false) }
    var showChangePassword by remember { mutableStateOf(false) }
    var showLegacyPlanInfo by remember { mutableStateOf(false) }
    var showGiftedPlanInfo by remember { mutableStateOf(false) }
    var showPackInfo by remember { mutableStateOf(false) }
    var showCancelFlow by remember { mutableStateOf(false) }
    var accountExpiry by remember { mutableStateOf<String?>(null) }
    var subscreen by remember(startOnSettings, startOnAccount) {
        mutableStateOf(when {
            startOnAccount -> ProfileSubscreen.Account
            startOnSettings -> ProfileSubscreen.Settings
            else -> null
        })
    }
    val usesEmailAuth = remember { SupabaseAuthService.instance.currentUserUsesEmailAuth }
    val scope = rememberCoroutineScope()
    val statsByType = history.groupingBy { it.mediaType }.eachCount()

    // Fetch the account expiry once when the screen mounts.
    LaunchedEffect(userEmail) {
        accountExpiry = fetchAccountExpiryWeb()
    }

    Div(attrs = {
        style {
            padding(32.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
        }
    }) {
        // Avatar, name, email + divider — only shown on the root "Profile" view, not subscreens.
        if (subscreen == null) {
            Div(attrs = {
                style {
                    width(80.px)
                    height(80.px)
                    borderRadius(50.percent)
                    property("overflow", "hidden")
                    property("border", "3px solid ${Accent}")
                    property("box-shadow", "0 4px 12px rgba(200, 150, 46, 0.25)")
                }
            }) {
                Img(
                    src = userPhotoUrl ?: "/images/default_avatar.png",
                    attrs = {
                        addEventListener("error") {
                            val img = it.target as? org.w3c.dom.HTMLImageElement
                            img?.src = "/images/default_avatar.png"
                        }
                        style {
                            width(100.percent)
                            height(100.percent)
                            property("object-fit", "cover")
                        }
                    }
                )
            }

            if (!userName.isNullOrBlank()) {
                Div(attrs = {
                    style { marginTop(12.px); fontSize(16.px); fontWeight(600); color(Gray900) }
                }) { Text(userName) }
                Div(attrs = {
                    style { marginTop(2.px); fontSize(13.px); fontWeight(400); color(Gray500) }
                }) { Text(userEmail ?: "No email") }
            } else {
                Div(attrs = {
                    style { marginTop(12.px); fontSize(15.px); fontWeight(500); color(Gray700) }
                }) { Text(userEmail ?: "No email") }
            }
            if (usesEmailAuth) {
                Div(attrs = {
                    onClick { showChangePassword = true }
                    style {
                        marginTop(10.px); fontSize(13.px); color(Primary); cursor("pointer")
                        fontWeight(500); property("transition", TransitionFast)
                    }
                }) { Text("Change Password") }
            }

            // Search Stats — shown directly on the Profile view
            Div(attrs = {
                style {
                    property("max-width", "280px"); width(100.percent)
                    height(1.px); backgroundColor(Gray200)
                    property("margin", "20px 0 16px")
                }
            })
            if (history.isEmpty()) {
                Div(attrs = {
                    style { property("text-align", "center"); padding(16.px, 0.px); color(Gray400); fontSize(14.px) }
                }) { Text("No searches yet") }
            } else {
                Div(attrs = {
                    style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); alignItems(AlignItems.Center) }
                }) {
                    SearchStatsHeatmap(history = history, onItemClick = onHistoryItemClick)
                    Div(attrs = { style { marginTop(12.px); fontSize(12.px); color(Gray500) } }) {
                        Text("${history.size} total ${if (history.size == 1) "search" else "searches"}")
                    }
                }
            }
            Div(attrs = { style { property("margin-top", "24px") } })
        }

        // Bottom area — switches between the nav menu and the two subscreens.
        // Push/pop animation: deeper-going content slides in from the right, popping back
        // slides in from the left. Direction is captured by tracking the previous state.
        // CSS keyframes are injected once at module load; key() forces a remount on each
        // subscreen change so the animation re-fires from frame 0.
        InjectProfileSubscreenKeyframes()
        var prevSubscreen by remember { mutableStateOf(subscreen) }
        val goingDeeper = prevSubscreen == null && subscreen != null
        SideEffect { prevSubscreen = subscreen }

        Div(attrs = {
            style {
                property("max-width", "280px")
                width(100.percent)
                property("overflow", "hidden")
            }
        }) {
            key(subscreen) {
                Div(attrs = {
                    style {
                        property(
                            "animation",
                            if (goingDeeper) "profileSlideInRight 250ms ease-out"
                            else "profileSlideInLeft 250ms ease-out"
                        )
                    }
                }) {
                    when (subscreen) {
                        null -> { /* Profile: only avatar/name/email shown above */ }
                        ProfileSubscreen.Settings -> ProfileSubscreenScaffold(
                            title = "Settings",
                            onBack = { subscreen = null }
                        ) {
                            DisplayStyleSettings(displaySettings, onDisplaySettingsChanged, history)
                        }
                        ProfileSubscreen.Account -> ProfileSubscreenScaffold(
                            title = "Account",
                            onBack = { subscreen = null }
                        ) {
                            val cardStyle: StyleScope.() -> Unit = {
                                width(100.percent); borderRadius(12.px)
                                backgroundColor(Gray100)
                                property("border", "1px solid #E5E7EB")
                                property("overflow", "hidden")
                            }
                            val rowStyle: StyleScope.() -> Unit = {
                                display(DisplayStyle.Flex); alignItems(AlignItems.Center); padding(10.px, 14.px)
                            }
                            val dividerStyle: StyleScope.() -> Unit = {
                                height(1.px); backgroundColor(Gray200); property("margin", "0 14px")
                            }

                            // Card-setup and reactivation still call the server directly from here;
                            // the billing summary and email preference themselves are loaded at APP
                            // OPEN by the ViewModel and passed in as parameters (see [billingSummary],
                            // [billingLoaded], [emailMarketing]) so this screen is already populated
                            // when it opens instead of growing under the user's cursor.
                            val billingApiClient = remember { createDefaultApiClient() }
                            var updatingCard by remember { mutableStateOf(false) }
                            var cardUpdateError by remember { mutableStateOf<String?>(null) }
                            var reactivating by remember { mutableStateOf(false) }
                            // The one flag that decides which world a member is in — see
                            // BillingSummary.manageable for the cohorts excluded (ThriveCart,
                            // legacy, gifted). Defaults to "not yet confirmed" while loading so the
                            // portal fallback below doesn't disappear before we know it's safe to.
                            val manageableInApp = billingSummary?.manageable == true

                            if (AppConfig.PAYMENTS_ENABLED && !billingLoaded) {
                                // Almost always already loaded — the ViewModel fetches this at app
                                // open. The spinner is for the narrow case of opening Account within
                                // a second of launch, or on a slow connection. Showing the half-built
                                // cards instead is what made rows appear under the user's cursor.
                                InjectSpinKeyframes()
                                Div(attrs = {
                                    style {
                                        width(100.percent); display(DisplayStyle.Flex)
                                        justifyContent(JustifyContent.Center); padding(40.px, 0.px)
                                    }
                                }) {
                                    Div(attrs = {
                                        style {
                                            width(22.px); height(22.px)
                                            property("border", "2px solid $Gray200")
                                            property("border-top-color", "$Primary")
                                            borderRadius(50.percent)
                                            property("animation", "spin 0.8s linear infinite")
                                        }
                                    })
                                }
                            } else if (AppConfig.PAYMENTS_ENABLED) {
                                val planKey = entitlement?.plan
                                val isGifted = entitlement?.isGifted == true
                                val planLabel = when {
                                    // An organisation account (set up by hand, never a subscriber): whatever else is on its row.
                                    entitlement?.isLibrary == true -> "Library"
                                    // A gift carries no plan key (plan stays NULL) — name it before the
                                    // generic fallbacks, or it reads as a bare "Premium".
                                    isGifted -> "Gifted Subscription"
                                    planKey != null -> plans.firstOrNull { it.key == planKey }?.name
                                        // "thrivecart_9" is the ThriveCart ($9/mo founding rate) cohort. It is
                                        // deliberately absent from pricing_config — adding it there would put a
                                        // buy button for a plan Stripe cannot sell into the plan switcher — so
                                        // this fallback is the ONLY place its name can come from.
                                        ?: when (planKey) { "basic_10" -> "Starter"; "pro_30" -> "Family Plan"; "annual" -> "Annual Plan"; "pro_29" -> "Pro Plan"; "thrivecart_9" -> "Thrive Plan"; else -> if (entitlement?.isUnlimited == true) "Membership" else "Premium" }
                                    isSubscribed -> "Premium"
                                    else -> "Not subscribed"
                                }
                                val noMembership = entitlement?.showNoActiveMembership == true
                                val isLegacy = entitlement?.status == "legacy"

                                AccountSectionLabel("SUBSCRIPTION")
                                if (entitlement == null) {
                                    Div(attrs = { style { fontSize(13.px); color(Gray500); padding(4.px, 0.px) } }) { Text("Loading…") }
                                } else if (noMembership) {
                                    Div(attrs = {
                                        style { cardStyle() }
                                    }) {
                                        Div(attrs = {
                                            onClick { onGetCredits() }
                                            style { padding(14.px); cursor("pointer"); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); alignItems(AlignItems.Center) }
                                        }) {
                                            Div(attrs = { style { fontSize(13.px); fontWeight(600); color(Gray600) } }) { Text("No Active Subscription") }
                                            Div(attrs = { style { fontSize(12.px); fontWeight(500); color(Primary); marginTop(2.px) } }) { Text("Tap here to subscribe") }
                                        }
                                    }
                                } else {
                                    // Subscription info card
                                    Div(attrs = { style { cardStyle() } }) {
                                        Div(attrs = { style { rowStyle() } }) {
                                            Div(attrs = { style { fontSize(13.px); color(Gray500) } }) { Text("Plan") }
                                            Div(attrs = { style { property("flex", "1") } })
                                            Div(attrs = { style { fontSize(13.px); color(Gray900); fontWeight(600) } }) { Text(planLabel) }
                                        }
                                        // A regular subscriber sees only their plan: no allowance, no "unlimited". Libraries see used/total.
                                        if (!entitlement.isUnlimited || entitlement.isLibrary) {
                                            Div(attrs = { style { dividerStyle() } })
                                            Div(attrs = { style { rowStyle() } }) {
                                                Div(attrs = { style { fontSize(13.px); color(Gray500) } }) { Text(if (entitlement.isLibrary) "Searches" else "Balance") }
                                                Div(attrs = { style { property("flex", "1") } })
                                                Div(attrs = { style { fontSize(13.px); color(Gray900) } }) { Text(entitlement.balanceLabel) }
                                            }
                                        }
                                        entitlement.currentPeriodEnd?.takeUnless { entitlement.isCanceledMembership }?.let { periodEnd ->
                                            val renewLabel = when (entitlement.status) {
                                                "active" -> "Renews"; "canceling" -> "Ends"; else -> null
                                            }
                                            if (renewLabel != null) {
                                                Div(attrs = { style { dividerStyle() } })
                                                Div(attrs = { style { rowStyle() } }) {
                                                    Div(attrs = { style { fontSize(13.px); color(Gray500) } }) { Text(renewLabel) }
                                                    Div(attrs = { style { property("flex", "1") } })
                                                    Div(attrs = { style { fontSize(13.px); color(Gray900) } }) { Text(formatExpiryDate(periodEnd)) }
                                                }
                                            }
                                        }
                                        if (entitlement.isPastDue) {
                                            Div(attrs = { style { dividerStyle() } })
                                            Div(attrs = { style { padding(10.px, 14.px); fontSize(12.px); fontWeight(600); property("color", "#B91C1C") } }) {
                                                Text("Payment past due. Update your card")
                                            }
                                        }
                                        // No "Access expires" line for an organisation holding a search pack (packs show their own balance).
                                    if ((isLegacy || isGifted) && !entitlement.isLibrary) {
                                            Div(attrs = { style { dividerStyle() } })
                                            Div(attrs = { style { rowStyle() } }) {
                                                Div(attrs = { style { fontSize(13.px); color(Gray500) } }) { Text("Access expires") }
                                                Div(attrs = { style { property("flex", "1") } })
                                                // A gift's expiry IS its unlimited window; legacy reads the approved_users date.
                                                val expiry = if (isGifted) entitlement.unlimitedUntil else accountExpiry
                                                if (expiry != null) {
                                                    Div(attrs = { style { fontSize(13.px); color(Gray900) } }) { Text(formatExpiryDate(expiry)) }
                                                } else {
                                                    var dots by remember { mutableStateOf(1) }
                                                    LaunchedEffect(Unit) {
                                                        while (true) {
                                                            kotlinx.coroutines.delay(400)
                                                            dots = if (dots >= 3) 1 else dots + 1
                                                        }
                                                    }
                                                    Div(attrs = { style { fontSize(13.px); color(Gray400) } }) { Text(".".repeat(dots)) }
                                                }
                                            }
                                        }
                                        // Payment method — only for the cohort we can actually write
                                        // to in Stripe. The in-app replacement for the hosted
                                        // portal's card-update page.
                                        //
                                        // Value and action on ONE row: "Update card" sat on its own
                                        // line below, which read as a separate setting rather than as
                                        // something you do to the card named above it. The expiry is
                                        // gone for the same reason — nobody acts on it, and it was
                                        // the only two-line value on the screen. Mirrors composeApp.
                                        if (manageableInApp) {
                                            Div(attrs = { style { dividerStyle() } })
                                            Div(attrs = { style { rowStyle() } }) {
                                                Div(attrs = { style { fontSize(13.px); color(Gray500) } }) { Text("Payment") }
                                                Div(attrs = { style { property("flex", "1") } })
                                                Div(attrs = { style { fontSize(13.px); color(Gray900) } }) {
                                                    Text(billingSummary?.card?.label ?: "No card on file")
                                                }
                                                Div(attrs = {
                                                if (!updatingCard) {
                                                    onClick {
                                                        updatingCard = true
                                                        cardUpdateError = null
                                                        scope.launch {
                                                            val intent = billingApiClient.createCardSetupIntent()
                                                            if (intent != null) {
                                                                val (secret, pk) = intent
                                                                // The HASH, never the query string: a query param
                                                                // rides in the request line and lands in server/CDN
                                                                // access logs and any Referer header, but a fragment
                                                                // never leaves the browser. /update-card reads it
                                                                // back with URLSearchParams(location.hash).
                                                                kotlinx.browser.window.location.href =
                                                                    "/update-card#cs=" + encodeURIComponent(secret) +
                                                                        "&pk=" + encodeURIComponent(pk)
                                                            } else {
                                                                updatingCard = false
                                                                cardUpdateError = "Couldn't open card update. Try again."
                                                            }
                                                        }
                                                    }
                                                }
                                                style {
                                                    property("margin-left", "12px")
                                                    fontSize(13.px); fontWeight(500)
                                                    color(Primary); cursor(if (updatingCard) "default" else "pointer")
                                                    if (updatingCard) property("opacity", "0.6")
                                                }
                                            }) { Text(if (updatingCard) "Opening…" else "Update") }
                                            }
                                            cardUpdateError?.let { err ->
                                                Div(attrs = { style { property("padding", "0 14px 10px"); fontSize(12.px); color(ScoreRed) } }) { Text(err) }
                                            }
                                        }
                                    }
                                    // A past-due thrivecart_9 member isn't `isSubscribed` (isActiveSubscriber
                                    // excludes past_due) and has no manageable card-on-file row above (their
                                    // billing lives on a separate Stripe account) — so without this OR they'd
                                    // have NO way to reach Manage Subscription at all. A main-account past-due
                                    // member (manageableInApp == true) already has the Payment/Update row
                                    // above, so this stays false for them and nothing changes.
                                    if (isSubscribed || entitlement.isLibrary || (entitlement.isPastDue && billingLoaded && !manageableInApp)) {
                                        Div(attrs = { style { height(6.px) } })
                                        Div(attrs = { style { cardStyle() } }) {
                                            if (billingLoading) {
                                                Div(attrs = { style { padding(10.px, 14.px); fontSize(13.px); color(Primary); fontWeight(500); property("opacity", "0.6") } }) { Text("Opening…") }
                                            } else {
                                                // Members we can manage in-app now do it through the
                                                // Payment/Update card rows above, so this row is only
                                                // the fallback for everyone else: legacy and gifted show
                                                // their own dialog, and anyone not yet confirmed
                                                // manageable still gets the portal rather than a dead end.
                                                var anyRowShown = false
                                                // Waits for the call rather than testing the summary alone. Most
                                // members ARE manageable, so showing the portal row first and pulling
                                // it away a moment later would be the common experience, not the edge
                                // case. A FAILED fetch still shows it: not knowing is a reason to
                                // leave the escape hatch in place, not to remove it.
                                if (billingLoaded && !manageableInApp) {
                                                    Div(attrs = {
                                                        onClick {
                                                            when {
                                                                // An organisation with a search pack: set up and changed by hand.
                                                                entitlement.isLibrary -> showPackInfo = true
                                                                isLegacy -> showLegacyPlanInfo = true
                                                                isGifted -> showGiftedPlanInfo = true
                                                                else -> onManageSubscription()
                                                            }
                                                        }
                                                        style { padding(10.px, 14.px); fontSize(13.px); color(Primary); fontWeight(500); cursor("pointer") }
                                                    }) { Text(if (entitlement.isLibrary) "Get help with your account" else "Manage Subscription") }
                                                    anyRowShown = true
                                                }
                                                // A paused membership's one useful action. Above the
                                                // cancel row because someone who paused is likelier to
                                                // come back than to leave — that's what pausing meant.
                                                if (entitlement.canResumeInApp) {
                                                    if (anyRowShown) Div(attrs = { style { dividerStyle() } })
                                                    Div(attrs = {
                                                        onClick { onResumeSubscription() }
                                                        style { padding(10.px, 14.px); fontSize(13.px); color(Primary); fontWeight(500); cursor("pointer") }
                                                    }) { Text("Resume subscription") }
                                                    anyRowShown = true
                                                }
                                                // Undo a scheduled cancellation. Only reachable while
                                                // status is "canceling" — the subscription is still live
                                                // through the period already paid for.
                                                if (entitlement.canReactivate) {
                                                    if (anyRowShown) Div(attrs = { style { dividerStyle() } })
                                                    Div(attrs = {
                                                        if (!reactivating) {
                                                            onClick {
                                                                reactivating = true
                                                                scope.launch {
                                                                    billingApiClient.reactivateSubscription()
                                                                    reactivating = false
                                                                    // Re-fetch rather than assume success — the
                                                                    // entitlement is the source of truth for
                                                                    // whether "canceling" actually cleared.
                                                                    onEntitlementChanged()
                                                                }
                                                            }
                                                        }
                                                        style {
                                                            padding(10.px, 14.px); fontSize(13.px); fontWeight(500)
                                                            color(Primary); cursor(if (reactivating) "default" else "pointer")
                                                            if (reactivating) property("opacity", "0.6")
                                                        }
                                                    }) { Text(if (reactivating) "Reactivating…" else "Reactivate subscription") }
                                                    anyRowShown = true
                                                }
                                                // The in-app exit. Only for members we can actually act
                                                // on: legacy, gifted and ThriveCart bill outside our
                                                // Stripe account, so for them this row leads nowhere.
                                                if (entitlement.canCancelInApp) {
                                                    if (anyRowShown) Div(attrs = { style { dividerStyle() } })
                                                    Div(attrs = {
                                                        onClick { showCancelFlow = true }
                                                        style { padding(10.px, 14.px); fontSize(13.px); color(Gray600); fontWeight(500); cursor("pointer") }
                                                    }) { Text("Cancel my subscription") }
                                                    anyRowShown = true
                                                }
                                            }
                                        }
                                    }
                                    // Billing history — its own card, only for the manageable cohort
                                    // and only when the server actually returned lines. Capped at
                                    // whatever GET /api/billing/summary sends back; no pagination.
                                    if (manageableInApp && billingSummary?.invoices?.isNotEmpty() == true) {
                                        AccountSectionLabel("BILLING HISTORY", topPadding = 18)
                                        Div(attrs = { style { cardStyle() } }) {
                                            billingSummary?.invoices.orEmpty().forEachIndexed { index, invoice ->
                                                if (index > 0) Div(attrs = { style { dividerStyle() } })
                                                Div(attrs = {
                                                    invoice.url?.let { url ->
                                                        onClick {
                                                            // Opens in a new tab so leaving the receipt page
                                                            // never loses the Account screen underneath. Third
                                                            // arg "noopener" is the same isolation a plain
                                                            // target="_blank" rel="noopener" link would give.
                                                            kotlinx.browser.window.open(url, "_blank", "noopener")
                                                        }
                                                    }
                                                    style {
                                                        rowStyle()
                                                        if (invoice.url != null) cursor("pointer")
                                                    }
                                                }) {
                                                    Div(attrs = { style { fontSize(13.px); color(Gray900) } }) {
                                                        Text(invoice.date?.let { formatExpiryDate(it) } ?: "-")
                                                    }
                                                    Div(attrs = { style { property("flex", "1") } })
                                                    if (!invoice.isPaid) {
                                                        invoice.problemLabel?.let { problem ->
                                                            Div(attrs = { style { fontSize(12.px); color(ScoreRed); marginRight(8.px) } }) { Text(problem) }
                                                        }
                                                    }
                                                    Div(attrs = { style { fontSize(13.px); color(Gray900); fontWeight(600) } }) { Text(invoice.amountLabel) }
                                                }
                                            }
                                        }
                                    }
                                }
                                Div(attrs = { style { height(12.px) } })
                            }

                            // Email preferences — always shown for a signed-in user, independent of
                            // AppConfig.PAYMENTS_ENABLED and of whatever membership state is above.
                            // Hidden entirely (rather than shown disabled) when the fetch failed, since
                            // a toggle we can't confirm the true state of is worse than no toggle.
                            emailMarketing?.let { marketingOn ->
                                AccountSectionLabel("EMAILS", topPadding = 6)
                                Div(attrs = { style { cardStyle() } }) {
                                    Div(attrs = { style { rowStyle() } }) {
                                        Div(attrs = { style { fontSize(13.px); color(Gray500) } }) { Text("Fitrah Filter news") }
                                        Div(attrs = { style { property("flex", "1") } })
                                        CheckboxInput(checked = marketingOn, attrs = {
                                            // The optimistic update and its revert-on-failure live in the
                                            // ViewModel, so web and mobile can't disagree about what a
                                            // failed save looks like.
                                            onChange { ev -> onEmailMarketingChanged(ev.value) }
                                            style { property("cursor", "pointer") }
                                        })
                                    }
                                    Div(attrs = { style { dividerStyle() } })
                                    Div(attrs = { style { property("padding", "8px 14px 10px"); fontSize(12.px); color(Gray500) } }) {
                                        Text("Account and billing emails are always sent.")
                                    }
                                }
                                Div(attrs = { style { height(12.px) } })
                            }

                            // Account actions card. Legacy AND gifted members already show "Access expires"
                            // in the membership card above — don't print it twice.
                            val isLegacyForBottomCard = entitlement?.status == "legacy" || entitlement?.isGifted == true
                            AccountSectionLabel("ACCOUNT", topPadding = 6)
                            Div(attrs = { style { cardStyle() } }) {
                                if (!isLegacyForBottomCard) {
                                    accountExpiry?.let { iso ->
                                        Div(attrs = { style { rowStyle() } }) {
                                            Div(attrs = { style { fontSize(13.px); color(Gray500) } }) { Text("Access expires") }
                                            Div(attrs = { style { property("flex", "1") } })
                                            Div(attrs = { style { fontSize(13.px); color(Gray700) } }) { Text(formatExpiryDate(iso)) }
                                        }
                                        Div(attrs = { style { dividerStyle() } })
                                    }
                                }
                                Div(attrs = {
                                    onClick { showDeleteConfirmation = true }
                                    style { padding(10.px, 14.px); fontSize(13.px); fontWeight(500); property("color", "#EF4444"); cursor("pointer") }
                                }) { Text("Delete Account") }
                            }
                        }
                    }
                }
            }
        }
    }

    if (showCancelFlow) {
        CancelSubscriptionFlow(
            onDismiss = { showCancelFlow = false },
            onChanged = onEntitlementChanged,
            // Members whose billing lives outside our Stripe account can't be cancelled here; the
            // flow bows out and the portal takes over rather than showing them a dead end.
            onSwitchToPortal = onManageSubscription,
        )
    }

    // Legacy founding member info popup
    if (showLegacyPlanInfo) {
        val expiryDate = accountExpiry?.let { formatExpiryDate(it) } ?: "your expiry date"
        Div(attrs = {
            onClick { showLegacyPlanInfo = false }
            style {
                property("position", "fixed"); property("inset", "0")
                backgroundColor(rgba(0, 0, 0, 0.5))
                display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                property("z-index", "1000")
            }
        }) {
            Div(attrs = {
                onClick { it.stopPropagation() }
                style {
                    backgroundColor(Color.white); borderRadius(16.px); padding(28.px)
                    property("max-width", "380px"); width(90.percent)
                    property("box-shadow", "0 20px 60px rgba(0,0,0,0.15)")
                }
            }) {
                // ThriveCart members share status "legacy" with the $100/yr founding cohort, but their
                // membership RENEWS rather than expiring — telling them to "select a new plan when
                // yours expires" would be plainly wrong, and they also can't manage it here (their
                // subscription lives in a Stripe account this app has no access to).
                val isThrive = entitlement?.plan == "thrivecart_9"
                Div(attrs = { style { fontSize(18.px); fontWeight(600); color(Gray900); marginBottom(12.px) } }) {
                    Text(if (isThrive) "Thrive Plan" else "Founding Member Plan")
                }
                Div(attrs = { style { fontSize(14.px); color(Gray600); property("line-height", "1.5"); marginBottom(24.px) } }) {
                    Text(
                        if (isThrive) {
                            val priceLabel = entitlement?.priceLabel
                            val opener = if (priceLabel != null)
                                "Your $priceLabel subscription renews automatically on $expiryDate. It's "
                            else
                                "Your subscription renews automatically on $expiryDate. It's "
                            opener + "billed outside the app, so there's nothing to manage here. Email " +
                                "support@fitrahfilter.com to change or cancel."
                        } else
                            "You can select a new plan when yours expires on $expiryDate."
                    )
                }
                Div(attrs = { style { display(DisplayStyle.Flex); justifyContent(JustifyContent.FlexEnd) } }) {
                    Div(attrs = {
                        onClick { showLegacyPlanInfo = false }
                        style {
                            padding(8.px, 20.px); borderRadius(8.px); backgroundColor(Primary)
                            color(Color.white); fontSize(14.px); fontWeight(500); cursor("pointer")
                        }
                    }) { Text("Got it") }
                }
            }
        }
    }

    // Organisation account (set up by hand): tells them to email support.
    if (showPackInfo) {
        Div(attrs = {
            onClick { showPackInfo = false }
            style {
                property("position", "fixed"); property("inset", "0")
                backgroundColor(rgba(0, 0, 0, 0.5))
                display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                property("z-index", "1000")
            }
        }) {
            Div(attrs = {
                onClick { it.stopPropagation() }
                style {
                    backgroundColor(Color.white); borderRadius(16.px); padding(28.px)
                    property("max-width", "380px"); width(90.percent)
                    property("box-shadow", "0 20px 60px rgba(0,0,0,0.15)")
                }
            }) {
                Div(attrs = { style { fontSize(18.px); fontWeight(600); color(Gray900); marginBottom(12.px) } }) { Text("Your account") }
                Div(attrs = { style { fontSize(14.px); color(Gray600); property("line-height", "1.5"); marginBottom(24.px) } }) {
                    Text("To change your plan or get help with your account, please email support@fitrahfilter.com.")
                }
                Div(attrs = { style { display(DisplayStyle.Flex); justifyContent(JustifyContent.FlexEnd) } }) {
                    Div(attrs = {
                        onClick { showPackInfo = false }
                        style {
                            padding(8.px, 20.px); borderRadius(8.px); backgroundColor(Primary)
                            color(Color.white); fontSize(14.px); fontWeight(500); cursor("pointer")
                        }
                    }) { Text("Got it") }
                }
            }
        }
    }

    // Gifted (comped invite) member info popup — same shape as the legacy one, since neither
    // has a Stripe subscription to manage.
    if (showGiftedPlanInfo) {
        val expiryDate = entitlement?.unlimitedUntil?.let { formatExpiryDate(it) } ?: "it expires"
        Div(attrs = {
            onClick { showGiftedPlanInfo = false }
            style {
                property("position", "fixed"); property("inset", "0")
                backgroundColor(rgba(0, 0, 0, 0.5))
                display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                property("z-index", "1000")
            }
        }) {
            Div(attrs = {
                onClick { it.stopPropagation() }
                style {
                    backgroundColor(Color.white); borderRadius(16.px); padding(28.px)
                    property("max-width", "380px"); width(90.percent)
                    property("box-shadow", "0 20px 60px rgba(0,0,0,0.15)")
                }
            }) {
                Div(attrs = { style { fontSize(18.px); fontWeight(600); color(Gray900); marginBottom(12.px) } }) {
                    Text("Complimentary Subscription")
                }
                Div(attrs = { style { fontSize(14.px); color(Gray600); property("line-height", "1.5"); marginBottom(24.px) } }) {
                    Text("You have access as a gift. There's nothing to pay and nothing to cancel. You can choose a plan when it expires on $expiryDate.")
                }
                Div(attrs = { style { display(DisplayStyle.Flex); justifyContent(JustifyContent.FlexEnd) } }) {
                    Div(attrs = {
                        onClick { showGiftedPlanInfo = false }
                        style {
                            padding(8.px, 20.px); borderRadius(8.px); backgroundColor(Primary)
                            color(Color.white); fontSize(14.px); fontWeight(500); cursor("pointer")
                        }
                    }) { Text("Got it") }
                }
            }
        }
    }

    // Change password dialog
    if (showChangePassword) {
        var newPassword by remember { mutableStateOf("") }
        var confirmPassword by remember { mutableStateOf("") }
        var isSubmitting by remember { mutableStateOf(false) }
        var errorMsg by remember { mutableStateOf<String?>(null) }
        var successMsg by remember { mutableStateOf<String?>(null) }

        Div(attrs = {
            onClick { if (!isSubmitting) showChangePassword = false }
            style {
                property("position", "fixed")
                property("inset", "0")
                backgroundColor(rgba(0, 0, 0, 0.5))
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                property("z-index", "1000")
            }
        }) {
            Div(attrs = {
                onClick { it.stopPropagation() }
                style {
                    backgroundColor(Color.white)
                    borderRadius(16.px)
                    padding(28.px)
                    property("max-width", "400px")
                    width(90.percent)
                    property("box-shadow", "0 20px 60px rgba(0,0,0,0.15)")
                }
            }) {
                Div(attrs = {
                    style {
                        fontSize(18.px); fontWeight(600); color(Gray900); marginBottom(16.px)
                    }
                }) { Text("Change Password") }

                // New password
                Input(type = org.jetbrains.compose.web.attributes.InputType.Password) {
                    value(newPassword)
                    onInput { newPassword = it.value }
                    attr("placeholder", "New password (min 6 chars)")
                    style {
                        width(100.percent); padding(12.px, 14.px); fontSize(14.px)
                        borderRadius(10.px); property("border", "1px solid ${Gray200}")
                        property("outline", "none"); property("box-sizing", "border-box")
                        marginBottom(10.px)
                    }
                }

                // Confirm
                Input(type = org.jetbrains.compose.web.attributes.InputType.Password) {
                    value(confirmPassword)
                    onInput { confirmPassword = it.value }
                    attr("placeholder", "Confirm new password")
                    style {
                        width(100.percent); padding(12.px, 14.px); fontSize(14.px)
                        borderRadius(10.px)
                        property("border", if (confirmPassword.isNotEmpty() && confirmPassword != newPassword) "1px solid $ScoreRed" else "1px solid $Gray200")
                        property("outline", "none"); property("box-sizing", "border-box")
                        marginBottom(8.px)
                    }
                }

                errorMsg?.let { err ->
                    Div(attrs = { style { color(ScoreRed); fontSize(12.px); marginBottom(8.px) } }) { Text(err) }
                }
                successMsg?.let { msg ->
                    Div(attrs = { style { color(Primary); fontSize(12.px); marginBottom(8.px) } }) { Text(msg) }
                }

                val passwordsMatch = newPassword.length >= 6 && newPassword == confirmPassword
                Div(attrs = {
                    style {
                        display(DisplayStyle.Flex); justifyContent(JustifyContent.FlexEnd); gap(12.px); marginTop(12.px)
                    }
                }) {
                    Div(attrs = {
                        onClick { if (!isSubmitting) showChangePassword = false }
                        style {
                            padding(8.px, 20.px); borderRadius(8.px); backgroundColor(Gray100)
                            color(Gray700); fontSize(14.px); fontWeight(500); cursor("pointer")
                        }
                    }) { Text("Cancel") }
                    Div(attrs = {
                        onClick {
                            if (passwordsMatch && !isSubmitting) {
                                isSubmitting = true
                                errorMsg = null
                                scope.launch {
                                    val result = SupabaseAuthService.instance.updatePassword(newPassword)
                                    isSubmitting = false
                                    result.fold(
                                        onSuccess = {
                                            successMsg = "Password updated successfully"
                                            kotlinx.browser.window.setTimeout({ showChangePassword = false }, 1500)
                                        },
                                        onFailure = { e ->
                                            errorMsg = e.message ?: "Failed to update password"
                                        }
                                    )
                                }
                            }
                        }
                        style {
                            padding(8.px, 20.px); borderRadius(8.px)
                            backgroundColor(if (passwordsMatch && !isSubmitting) Primary else Gray300)
                            color(Color.white); fontSize(14.px); fontWeight(500)
                            cursor(if (passwordsMatch && !isSubmitting) "pointer" else "not-allowed")
                        }
                    }) { Text(if (isSubmitting) "Updating..." else "Update") }
                }
            }
        }
    }

    // Confirmation dialog
    if (showDeleteConfirmation) {
        // Overlay
        Div(attrs = {
            onClick { showDeleteConfirmation = false }
            style {
                property("position", "fixed")
                property("inset", "0")
                backgroundColor(rgba(0, 0, 0, 0.5))
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                property("z-index", "1000")
            }
        }) {
            // Dialog box
            Div(attrs = {
                onClick { it.stopPropagation() }
                style {
                    backgroundColor(Color.white)
                    borderRadius(16.px)
                    padding(28.px)
                    property("max-width", "400px")
                    width(90.percent)
                    property("box-shadow", "0 20px 60px rgba(0,0,0,0.15)")
                }
            }) {
                Div(attrs = {
                    style {
                        fontSize(18.px)
                        fontWeight(600)
                        color(Gray900)
                        marginBottom(12.px)
                    }
                }) { Text("Delete Account") }

                Div(attrs = {
                    style {
                        fontSize(14.px)
                        color(Gray600)
                        property("line-height", "1.5")
                        marginBottom(24.px)
                    }
                }) { Text("Are you sure you want to permanently delete your account? This action cannot be undone.") }

                Div(attrs = {
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.FlexEnd)
                        gap(12.px)
                    }
                }) {
                    // Cancel button
                    Div(attrs = {
                        onClick { showDeleteConfirmation = false }
                        style {
                            padding(8.px, 20.px)
                            borderRadius(8.px)
                            backgroundColor(Gray100)
                            color(Gray700)
                            fontSize(14.px)
                            fontWeight(500)
                            cursor("pointer")
                        }
                    }) { Text("Cancel") }

                    // Delete button
                    Div(attrs = {
                        onClick {
                            showDeleteConfirmation = false
                            onDeleteAccount()
                        }
                        style {
                            padding(8.px, 20.px)
                            borderRadius(8.px)
                            backgroundColor(Color("#EF4444"))
                            color(Color.white)
                            fontSize(14.px)
                            fontWeight(500)
                            cursor("pointer")
                        }
                    }) { Text("Delete") }
                }
            }
        }
    }
}

private enum class ProfileSubscreen { Settings, Account }

/**
 * Injects the @keyframes used by the profile subscreen push/pop transitions exactly once.
 * Compose HTML doesn't have AnimatedContent, so we rely on plain CSS keyframe animations
 * applied to a key()-remounted wrapper — each subscreen change replaces the DOM element,
 * which causes the browser to play the animation from frame 0.
 */
@Composable
private fun InjectProfileSubscreenKeyframes() {
    LaunchedEffect(Unit) {
        val styleId = "profile-subscreen-keyframes"
        val doc = kotlinx.browser.document
        if (doc.getElementById(styleId) != null) return@LaunchedEffect
        val style = doc.createElement("style") as org.w3c.dom.HTMLStyleElement
        style.id = styleId
        // textContent (not innerHTML) — content is a static literal but the browser parses
        // CSS rules from <style>.textContent the same way.
        style.textContent = """
            @keyframes profileSlideInRight {
                from { transform: translateX(24px); opacity: 0; }
                to   { transform: translateX(0);    opacity: 1; }
            }
            @keyframes profileSlideInLeft {
                from { transform: translateX(-24px); opacity: 0; }
                to   { transform: translateX(0);     opacity: 1; }
            }
        """.trimIndent()
        doc.head?.appendChild(style)
    }
}

/**
 * Small uppercase heading above a group of cards.
 *
 * The Account page used to be an undifferentiated stack of five cards with no hierarchy — the
 * subscription, its history, an email toggle and a delete button all looked equally important.
 * These headings say which of them belong together, and cost one line each.
 *
 * Same treatment as the Settings screen's section labels, deliberately: two screens one tap apart
 * should not each invent their own idea of what a heading looks like.
 */
@Composable
private fun AccountSectionLabel(text: String, topPadding: Int = 0) {
    Div(attrs = {
        style {
            fontSize(12.px)
            fontWeight(700)
            color(Gray500)
            property("letter-spacing", "0.5px")
            property("text-transform", "uppercase")
            width(100.percent)
            property("text-align", "left")
            marginTop(topPadding.px)
            marginBottom(8.px)
        }
    }) { Text(text) }
}

/** Subscreen wrapper: small Material-style back arrow + centered title, then content. */
@Composable
private fun ProfileSubscreenScaffold(
    title: String,
    onBack: () -> Unit,
    centerContent: Boolean = false,
    content: @Composable () -> Unit
) {
    Span(attrs = {
        style {
            display(DisplayStyle.Block)
            fontSize(14.px); fontWeight(600); color(Gray900)
            property("letter-spacing", "0.02em")
            property("text-align", "center")
            property("width", "100%")
            marginBottom(12.px)
        }
    }) { Text(title) }
    if (centerContent) {
        // Reserve a generous min-height so a sparse content list (e.g. Manage Account
        // with two short text rows) appears vertically centered rather than clustered
        // at the top of the available space.
        Div(attrs = {
            style {
                property("min-height", "220px")
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
            }
        }) { content() }
    } else {
        Div { content() }
    }
}

/**
 * Heatmap matrix mirroring the mobile equivalent: rows = MediaTypes the user has searched,
 * columns = score buckets (Safe/Caution/Not Safe). Each cell's background is the bucket's
 * score color tinted by `count / maxCount` opacity. Tap a cell to reveal the count for ~2s.
 */
@Composable
private fun SearchStatsHeatmap(
    history: List<HistoryItem>,
    onItemClick: (HistoryItem) -> Unit = {}
) {
    data class Bucket(val label: String, val icon: String, val r: Int, val g: Int, val b: Int, val range: IntRange)
    val buckets = listOf(
        Bucket("Safe", "✓", 34, 197, 94, 7..10),
        Bucket("Caution", "~", 245, 158, 11, 4..6),
        Bucket("Unsafe", "✕", 239, 68, 68, 1..3)
    )

    val counts: Map<Pair<MediaType, Int>, Int> = history.groupingBy {
        val score = it.result.score
        val bIdx = buckets.indexOfFirst { b -> score in b.range }.coerceAtLeast(0)
        it.mediaType to bIdx
    }.eachCount()

    val activeTypes = MediaType.entries
        .filter { type -> history.any { it.mediaType == type } }
        .sortedWith(
            compareByDescending<MediaType> { counts[it to 0] ?: 0 }   // most Safe first
                .thenByDescending { counts[it to 1] ?: 0 }             // then most Caution
                .thenBy { counts[it to 2] ?: 0 }                      // then least Unsafe
        )
    if (activeTypes.isEmpty()) return

    val maxCount = (counts.values.maxOrNull() ?: 1).coerceAtLeast(1)
    var dialogCell by remember { mutableStateOf<Triple<MediaType, Int, String>?>(null) }

    Div(attrs = {
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "80px repeat(3, 44px)")
            property("gap", "6px")
            property("align-items", "center")
        }
    }) {
        // Header row — empty corner + 3 icon labels
        Div { /* spacer for label column */ }
        buckets.forEach { b ->
            Div(attrs = {
                style {
                    fontSize(16.px)
                    fontWeight(700)
                    color(rgb(b.r, b.g, b.b))
                    property("text-align", "center")
                }
            }) { Text(b.icon) }
        }

        // Data rows
        activeTypes.forEach { type ->
            Div(attrs = {
                style {
                    fontSize(12.px)
                    color(Gray700)
                }
            }) { Text(type.label) }

            buckets.forEachIndexed { idx, b ->
                val count = counts[type to idx] ?: 0
                val alpha = if (count == 0) 0.0 else (0.12 + 0.88 * count.toDouble() / maxCount.toDouble())
                Div(attrs = {
                    if (count > 0) {
                        onClick { dialogCell = Triple(type, idx, b.label) }
                    }
                    style {
                        width(44.px)
                        height(44.px)
                        borderRadius(8.px)
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        if (count > 0) {
                            property("background", "rgba(${b.r}, ${b.g}, ${b.b}, $alpha)")
                            cursor("pointer")
                        } else {
                            property("border", "1px solid ${Gray200}")
                        }
                    }
                }) {
                    Span(attrs = {
                        style {
                            fontSize(13.px)
                            fontWeight(700)
                            color(Color.white)
                            // 1px gray text outline via 4-direction shadow stack — visible on
                            // every cell color including empty cells.
                            property(
                                "text-shadow",
                                "-1px -1px 0 #6B7280, 1px -1px 0 #6B7280, -1px 1px 0 #6B7280, 1px 1px 0 #6B7280"
                            )
                        }
                    }) { Text("$count") }
                }
            }
        }
    }

    // Cell-tap dialog — modal listing every history title that falls into the tapped bucket.
    dialogCell?.let { (type, bIdx, bucketLabel) ->
        val bucket = buckets[bIdx]
        val matching = history.filter { it.mediaType == type && it.result.score in bucket.range }
            .sortedByDescending { it.timestamp }
        Div(attrs = {
            onClick { dialogCell = null }
            style {
                property("position", "fixed")
                property("inset", "0")
                backgroundColor(rgba(0, 0, 0, 0.5))
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                property("z-index", "1000")
            }
        }) {
            Div(attrs = {
                onClick { it.stopPropagation() }
                style {
                    backgroundColor(Color.white)
                    borderRadius(16.px)
                    padding(24.px)
                    property("max-width", "400px")
                    width(90.percent)
                    property("max-height", "70vh")
                    property("overflow-y", "auto")
                    property("box-shadow", "0 20px 60px rgba(0,0,0,0.15)")
                    property("position", "relative")
                }
            }) {
                // Close X in the top-right corner
                Div(attrs = {
                    onClick { dialogCell = null }
                    style {
                        property("position", "absolute")
                        property("top", "10px")
                        property("right", "10px")
                        width(28.px)
                        height(28.px)
                        borderRadius(14.px)
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        cursor("pointer")
                        color(Gray500)
                        fontSize(20.px)
                        property("font-weight", "300")
                        property("line-height", "1")
                    }
                }) { Text("\u2715") }

                Div(attrs = {
                    style {
                        fontSize(15.px)
                        fontWeight(600)
                        color(Gray900)
                        marginBottom(12.px)
                        property("padding-right", "32.px")
                    }
                }) { Text("${type.label} • $bucketLabel") }

                matching.forEach { item ->
                    Div(attrs = {
                        onClick {
                            dialogCell = null
                            onItemClick(item)
                        }
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            padding(8.px, 6.px)
                            borderRadius(8.px)
                            cursor("pointer")
                            property("transition", "background-color 0.15s ease")
                        }
                    }) {
                        // Tiny score chip
                        Div(attrs = {
                            style {
                                width(28.px)
                                height(28.px)
                                borderRadius(6.px)
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                justifyContent(JustifyContent.Center)
                                property("background", "rgba(${bucket.r}, ${bucket.g}, ${bucket.b}, 0.15)")
                                property("flex-shrink", "0")
                            }
                        }) {
                            Span(attrs = {
                                style {
                                    fontSize(12.px)
                                    fontWeight(700)
                                    color(rgb(bucket.r, bucket.g, bucket.b))
                                }
                            }) { Text("${item.result.score}") }
                        }
                        Span(attrs = {
                            style {
                                fontSize(14.px)
                                color(Gray900)
                                marginLeft(10.px)
                                property("overflow", "hidden")
                                property("text-overflow", "ellipsis")
                                property("white-space", "nowrap")
                            }
                        }) { Text(item.title) }
                    }
                }
            }
        }
    }
}

/**
 * Web-side equivalent of AnthropicApiClient.fetchAccountExpiry — uses window.fetch directly
 * because the site module doesn't depend on the ktor client. Returns null on any failure
 * so the profile row hides itself rather than showing an error.
 */
private suspend fun fetchAccountExpiryWeb(): String? {
    return try {
        val token = try { SupabaseAuthService.instance.getFreshAccessToken() } catch (_: Exception) { null }
            ?: return null
        val baseUrl = com.islamicmedia.shared.data.api.serverBaseUrl()
        val headers = kotlin.js.json("Authorization" to "Bearer $token", "Accept" to "application/json")
        val response = kotlinx.browser.window.fetch(
            "$baseUrl/api/account/expiry",
            kotlin.js.json("method" to "GET", "headers" to headers).unsafeCast<org.w3c.fetch.RequestInit>()
        ).await()
        if (!response.ok) return null
        val text = response.text().await()
        val obj = kotlinx.serialization.json.Json.parseToJsonElement(text.toString()) as? kotlinx.serialization.json.JsonObject
            ?: return null
        val prim = obj["expiry_date"] as? kotlinx.serialization.json.JsonPrimitive
        if (prim == null || prim.toString() == "null") null else prim.content
    } catch (_: Throwable) { null }
}

/** Format an ISO timestamp like "2027-03-04T..." → "Mar 4, 2027". Falls back to YYYY-MM-DD on parse error. */
private fun formatExpiryDate(iso: String): String {
    return try {
        val datePart = iso.substringBefore('T')
        val date = kotlinx.datetime.LocalDate.parse(datePart)
        val month = when (date.monthNumber) {
            1 -> "Jan"; 2 -> "Feb"; 3 -> "Mar"; 4 -> "Apr"
            5 -> "May"; 6 -> "Jun"; 7 -> "Jul"; 8 -> "Aug"
            9 -> "Sep"; 10 -> "Oct"; 11 -> "Nov"; 12 -> "Dec"
            else -> "?"
        }
        "$month ${date.dayOfMonth}, ${date.year}"
    } catch (_: Exception) {
        iso.substringBefore('T')
    }
}
